/**
 * Type-safe Error Utilities
 *
 * Helpers for handling unknown errors in catch blocks,
 * checking error types, and extracting error information.
 */

/**
 * Type guard to check if a value is an Error instance
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Type guard for errors with a code property (like Node.js errors)
 */
export interface ErrorWithCode extends Error {
  code: string;
}

export function isErrorWithCode(error: unknown): error is ErrorWithCode {
  return (
    isError(error) &&
    'code' in error &&
    typeof (error as ErrorWithCode).code === 'string'
  );
}

/**
 * Type for HTTP errors with response data
 */
export interface ApiError extends Error {
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
    headers?: Record<string, string>;
  };
  statusCode?: number;
}

export function isApiError(error: unknown): error is ApiError {
  return isError(error) && ('response' in error || 'statusCode' in error);
}

function isHttpError(error: unknown): error is ApiError {
  return isApiError(error);
}

/**
 * Get the error message from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (isError(error)) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unknown error occurred';
}

/**
 * Get error details for logging
 */
export function getErrorDetails(error: unknown): {
  message: string;
  code?: string;
  status?: number;
  data?: unknown;
  stack?: string;
} {
  const message = getErrorMessage(error);

  const details: {
    message: string;
    code?: string;
    status?: number;
    data?: unknown;
    stack?: string;
  } = { message };

  if (isErrorWithCode(error)) {
    details.code = error.code;
  }

  if (isHttpError(error)) {
    details.status = error.response?.status ?? error.statusCode;
    details.data = error.response?.data;
  }

  if (isError(error)) {
    details.stack = error.stack;
  }

  return details;
}

/**
 * Check if error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  if (isErrorWithCode(error)) {
    return ['ECONNREFUSED', 'ETIMEDOUT', 'ENOTFOUND', 'ENETUNREACH'].includes(
      error.code
    );
  }
  return false;
}

/**
 * Check if error is a retryable HTTP error
 */
export function isRetryableHttpError(error: unknown): boolean {
  if (isNetworkError(error)) {
    return true;
  }

  if (isHttpError(error)) {
    const status = error.response?.status ?? error.statusCode;
    // Retry on 5xx server errors and 429 rate limit
    if (status && (status >= 500 || status === 429)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if error is a transient browser fetch interruption
 * caused by navigation, unmount, or aborted background reads.
 */
export function isTransientFetchError(error: unknown): boolean {
  if (!isError(error)) {
    return false;
  }

  return (
    error.name === 'AbortError' ||
    error.message === 'Failed to fetch' ||
    error.message === 'Load failed'
  );
}
